import type { ReactNode } from "react";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import AlertStatusTag from "../Alerts/AlertStatusTag";
import type { AlertProps, InsightProps } from "./insights";
import { getAlertDisplayCondition, getAlertStatus } from "./utils";
import InsightRunExplanation from "./InsightRunExplanation";
import ExplanationDropdown from "./ExplanationDropdown";
import InsightAnswer from "./InsightAnswer";

const LabelledBlock = ({
  label,
  aside,
  children,
}: {
  label: string;
  aside?: ReactNode;
  children: string;
}) => (
  <div className="flex flex-col bg-[#0000000f] p-3 rounded-sm">
    <div className="flex items-center justify-between gap-3 mb-1">
      <p className="text-sm text-[#00000073]">{label}</p>
      {aside}
    </div>
    <p className="text-sm text-[#2B4752] whitespace-pre-wrap">{children}</p>
  </div>
);

export function InsightSection({ insight }: { insight: InsightProps }) {
  return (
    <div data-testid="insight-drawer-insight-section" className="mb-7">
      <h3 className="text-base font-medium text-black mb-2">Insight</h3>
      <div className="flex flex-col gap-3 bg-white p-4 rounded-lg shadow-md">
        <LabelledBlock label="INSIGHT PROMPT">
          {insight.av_question || "Not available"}
        </LabelledBlock>
        <div className="px-3">
          <InsightAnswer answer={insight.av_answer} />
        </div>
        <ExplanationDropdown label="INSIGHT EXPLANATION">
          <InsightRunExplanation run={insight} />
        </ExplanationDropdown>
      </div>
    </div>
  );
}

export function AlertSection({ alert }: { alert: AlertProps }) {
  return (
    <div data-testid="insight-drawer-alert-section" className="mb-7">
      <h3 className="text-base font-medium text-black mb-2">Alert</h3>
      <div className="flex flex-col gap-3 bg-white p-4 rounded-lg shadow-md">
        <LabelledBlock
          label="ALERT ME IF"
          aside={<AlertStatusTag status={getAlertStatus(alert)} />}
        >
          {getAlertDisplayCondition(alert)}
        </LabelledBlock>
        <ExplanationDropdown label="ALERT EXPLANATION">
          {alert.condition_met_detail ? (
            <div className="markdown text-sm text-[#2B4752]">
              <MarkdownRenderer content={alert.condition_met_detail} />
            </div>
          ) : (
            <p className="text-sm text-[#00000073]">
              No explanation available.
            </p>
          )}
        </ExplanationDropdown>
      </div>
    </div>
  );
}
